import { useEffect, useRef } from 'react';
import autosize from 'autosize';

export const useTextAreaAutosize = (value: string | undefined) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;
    autosize(textarea);

    let rafId = 0;
    let observer: ResizeObserver | undefined;
    // mounted hidden (eg a closed popover) means autosize measured 0. wait for
    // the textarea to gain layout, recompute once, then stop
    if (textarea.offsetWidth === 0 && textarea.offsetHeight === 0) {
      observer = new ResizeObserver(entries => {
        const rect = entries[0]?.contentRect;
        if (!rect || (rect.width === 0 && rect.height === 0)) return;
        observer?.disconnect();
        rafId = requestAnimationFrame(() => autosize.update(textarea));
      });
      observer.observe(textarea);
    }

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      autosize.destroy(textarea);
    };
  }, []);

  useEffect(() => {
    if (ref.current) autosize.update(ref.current);
  }, [value]);

  return { ref };
};

type ChildElementSuffix = 'error' | 'help-text' | 'hint' | 'warning';

export const getChildElementId = (
  inputId: string | undefined,
  suffix: ChildElementSuffix
): string | undefined =>
  inputId !== undefined ? `${inputId}-${suffix}` : undefined;
